import React, { useState } from 'react';
import {
  Box,
  Input,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';

import { MdBusiness, MdPerson, MdEmail, MdNumbers, MdPhone } from 'react-icons/md';
import GovernmentDataApi from '../../../../api/governmentData';


const ContactDetailsStep = ({ data, updateData, errors, touched }) => {
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(false);
  const [companySearchResult, setCompanySearchResult] = useState(null);
  const toast = useToast();
  // Color mode values
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = useColorModeValue('gray.400', 'white');
  const primaryColor = useColorModeValue('teal.500', 'teal.500');
  const lightBg = useColorModeValue('teal.50', 'teal.900');
  const lightBorder = useColorModeValue('teal.100', 'teal.800');

  const handleCompanyIdSearch = async (companyId) => {
    if (!companyId || companyId.length !== 9) return;

    setIsLoadingCompanyData(true);
    setCompanySearchResult(null);

    try {
      const result = await GovernmentDataApi.getCompanyByGovId(companyId);

      if (result.success) {
        const company = result.company;
        setCompanySearchResult(result);

        // Auto-fill company data
        updateData({
          companyName: company['שם חברה'] || company.company_name || '',
          companyAddress: company['כתובת'] || company.address || ''
        });

        toast({
          title: 'נמצאה חברה',
          description: `נמצאה חברה: ${company['שם חברה'] || company.company_name}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        setCompanySearchResult(result);
        toast({
          title: 'חברה לא נמצאה',
          description: result.errorMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error searching company:', error);
      toast({
        title: 'שגיאה בחיפוש',
        description: 'אירעה שגיאה בחיפוש החברה. אנא נסו שוב.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCompanyData(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Step Title */}
      <Box textAlign="center" mb={4}>
        <Box
          w="16"
          h="16"
          bg={primaryColor}
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mx="auto"
          mb={4}
        >
          <MdBusiness size="32px" color="white" />
        </Box>
        <Text
          fontSize={{ base: '20px', md: '24px' }}
          fontWeight="700"
          color={textColor}
          mb={2}
        >
          ברוכים הבאים ל-Payro
        </Text>
        <Text
          fontSize="16px"
          color={textColorSecondary}
          fontWeight="400"
        >
          אנא מלאו את הפרטים הבאים כדי להתחיל
        </Text>
      </Box>

      {/* Form Fields Grid */}
      <Box maxW="1000px" mx="auto">
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
        {/* Full Name Field */}
        <GridItem>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between" align="center">
              <Text
                fontSize="14px"
                fontWeight="500"
                color={textColor}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <MdPerson size="16px" />
                שם מלא של איש הקשר
              </Text>
            </HStack>
            <Input
              value={data.fullName || ''}
              onChange={(e) => updateData({ fullName: e.target.value })}
              placeholder="אבי כהן"
              size="lg"
              fontSize="16px"
              w="100%"
              minW="280px"
              bg="white"
              border="1px solid"
              borderColor={errors.fullName && touched.fullName ? 'red.300' : 'gray.300'}
              borderRadius="8px"
              h="48px"
              _hover={{
                borderColor: 'gray.200',
              }}
              _focus={{
                borderColor: 'teal.500',
                boxShadow: '0 0 0 1px teal.200',
              }}
              _placeholder={{
                color: 'gray.400',
                fontSize: '14px',
              }}
            />
            {errors.fullName && touched.fullName && (
              <Text fontSize="12px" color="red.500">
                {errors.fullName}
              </Text>
            )}
          </VStack>
        </GridItem>

        {/* Email Field */}
        <GridItem>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between" align="center">
              <Text
                fontSize="14px"
                fontWeight="500"
                color={textColor}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <MdEmail size="16px" />
                כתובת אימייל
              </Text>
            </HStack>
            <Input
              value={data.email || ''}
              onChange={(e) => updateData({ email: e.target.value })}
              onBlur={(e) => updateData({ email: e.target.value })}
              placeholder="example@company.co.il"
              type="email"
              size="lg"
              fontSize="16px"
              w="100%"
              minW="280px"
              bg="white"
              border="1px solid"
              borderColor={errors.email && touched.email ? 'red.300' : 'gray.300'}
              borderRadius="8px"
              h="48px"
              dir="ltr"
              textAlign="left"
              _hover={{
                borderColor: 'gray.200',
              }}
              _focus={{
                borderColor: 'teal.500',
                boxShadow: '0 0 0 1px teal.200',
              }}
              _placeholder={{
                color: 'gray.400',
                fontSize: '14px',
              }}
            />
            {errors.email && touched.email && (
              <Text fontSize="12px" color="red.500">
                {errors.email}
              </Text>
            )}
          </VStack>
        </GridItem>

        {/* Company ID Field with Government API Integration */}
        <GridItem>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between" align="center">
              <Text
                fontSize="14px"
                fontWeight="500"
                color={textColor}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <MdNumbers size="16px" />
                ח.פ
              </Text>
            </HStack>
            <HStack>
              <Input
                value={data.companyId || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  updateData({ companyId: value });
                  if (value.length === 9) {
                    handleCompanyIdSearch(value);
                  }
                }}
                placeholder="123456789"
                maxLength={9}
                size="lg"
                fontSize="16px"
                w="100%"
                minW="280px"
                bg="white"
                border="1px solid"
                borderColor={errors.companyId && touched.companyId ? 'red.300' : 'gray.300'}
                borderRadius="8px"
                h="48px"
                dir="ltr"
                textAlign="left"
              _hover={{
                borderColor: 'gray.200',
              }}
              _focus={{
                borderColor: 'teal.500',
                boxShadow: '0 0 0 1px teal.200',
              }}
              _placeholder={{
                color: 'gray.400',
                fontSize: '14px',
              }}
              />
              {isLoadingCompanyData && <Spinner size="sm" color="teal.500" />}
            </HStack>
            {errors.companyId && touched.companyId && (
              <Text fontSize="12px" color="red.500">
                {errors.companyId}
              </Text>
            )}
          </VStack>
        </GridItem>

        {/* Contact Phone Field */}
        <GridItem>
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between" align="center">
              <Text
                fontSize="14px"
                fontWeight="500"
                color={textColor}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <MdPhone size="16px" />
                טלפון איש הקשר
              </Text>
            </HStack>
            <Input
              value={data.contactPhone || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                updateData({ contactPhone: value });
              }}
              placeholder="0501234567"
              maxLength={10}
              size="lg"
              fontSize="16px"
              w="100%"
              minW="280px"
              bg="white"
              border="1px solid"
              borderColor={errors.contactPhone && touched.contactPhone ? 'red.300' : 'gray.300'}
              borderRadius="8px"
              h="48px"
              dir="ltr"
              textAlign="left"
              _hover={{
                borderColor: 'gray.200',
              }}
              _focus={{
                borderColor: 'teal.500',
                boxShadow: '0 0 0 1px teal.200',
              }}
              _placeholder={{
                color: 'gray.400',
                fontSize: '14px',
              }}
            />
            {errors.contactPhone && touched.contactPhone && (
              <Text fontSize="12px" color="red.500">
                {errors.contactPhone}
              </Text>
            )}
          </VStack>
        </GridItem>
        </Grid>
      </Box>

      {/* Government API Search Result */}
      {companySearchResult && (
        <Alert
          status={companySearchResult.success ? 'success' : 'warning'}
          borderRadius="12px"
        >
          <AlertIcon />
          <Box>
            <AlertTitle>
              {companySearchResult.success ? 'נמצאה חברה!' : 'חברה לא נמצאה'}
            </AlertTitle>
            <AlertDescription>
              {companySearchResult.success
                ? `נמצאה חברה: ${companySearchResult.company['שם חברה'] || companySearchResult.company.company_name}`
                : companySearchResult.errorMessage
              }
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Info Box - Lovable Style */}
      <Box
        bg={lightBg}
        border="1px solid"
        borderColor={lightBorder}
        borderRadius="12px"
        p={6}
      >
        <Text
          fontSize="16px"
          fontWeight="600"
          color={textColor}
          mb={3}
          textAlign="right"
        >
          מה קורה הלאה?
        </Text>
        <VStack align="start" spacing={2} textAlign="right">
          <Text fontSize="14px" color={textColorSecondary}>
            • נבצע אימות מהיר של פרטי החברה
          </Text>
          <Text fontSize="14px" color={textColorSecondary}>
            • נאשר את התנאים המסחריים
          </Text>
          <Text fontSize="14px" color={textColorSecondary}>
            • תקבלו גישה מיידית למערכת
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
};

export default ContactDetailsStep;
